import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Image, Filter, Download, Undo2, Bot, X } from "lucide-react";
import PageTitle from "../components/common/PageTitle";
import DataTable from "../components/common/DataTable";
import FilterDropdown from "../components/common/FilterDropdown";
import SearchBar from "../components/common/SearchBar";
import MobileFilterPanel from "../components/common/MobileFilterPanel";
import ExportExcelModal from "../components/modals/ExportExcelModal";
import { formatDateShort } from "../utils/dateFormat";
import { showError} from "../utils/toast";
import { PAGINATION, ROUTES, JWT } from "../config/constants";
import { getCategoryName, getCategoryColor } from "../utils/categoryHelpers";
import { API_BASE_URL } from "../api/apiClient";

import LessonLearnedChat from "./LessonLearnedChat";

export default function LessonDashboard() {
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const [isChatOpen, setIsChatOpen] = useState(false);

  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [tempCategory, setTempCategory] = useState("");
  const [tempProject, setTempProject] = useState("");
  const [tempSort, setTempSort] = useState("newest");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLessons, setTotalLessons] = useState(0);
  const limit = PAGINATION.DEFAULT_PAGE_SIZE;

  const [showExportModal, setShowExportModal] = useState(false);

  useEffect(() => {
    loadCategories();
    loadProjects();
    loadStats();

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchKeyword(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    loadLessons();
  }, [page, selectedCategory, selectedProject, sortBy, searchKeyword]);

  async function loadCategories() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/categories`);
      const data = await response.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load categories:", err);
    }
  }

  async function loadProjects() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/project-names`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(JWT.TOKEN_KEY)}`,
        },
      });

      const data = await response.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load projects:", err);
      showError("Gagal memuat daftar project");
    }
  }

  async function loadStats() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/dashboard/stats`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  }

  async function loadLessons() {
    setLoading(true);
    try {
      // Menggunakan append agar lebih aman dari konversi tipe data
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      params.append("sort_by", sortBy);

      if (selectedCategory) params.append("category_id", selectedCategory);
      
      // CATATAN PENTING: 
      // Jika backend public Anda menerima ID, biarkan "project_id". 
      // Jika backend menerima Nama, ubah menjadi "project" atau "project_name" sesuai API Anda.
      if (selectedProject) params.append("project_id", selectedProject); 
      
      if (searchKeyword) params.append("search", searchKeyword);

      const response = await fetch(
        `${API_BASE_URL}/api/public/lessons?${params.toString()}`
      );
      const data = await response.json();

      setLessons(data.lessons || []);
      setTotalLessons(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error("Failed to load lessons:", err);
      showError("Gagal memuat data lessons");
      setLessons([]);
      setTotalLessons(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }

  function handleCategoryChange(val) {
    if (isMobile) setTempCategory(val);
    else {
      setSelectedCategory(val);
      setPage(1);
    }
  }

  function handleProjectChange(val) {
    if (isMobile) setTempProject(val);
    else {
      setSelectedProject(val);
      setPage(1);
    }
  }

  function handleSortChange(val) {
    if (isMobile) setTempSort(val);
    else {
      setSortBy(val);
      setPage(1);
    }
  }

  function handleApplyFilters() {
    setSelectedCategory(tempCategory);
    setSelectedProject(tempProject);
    setSortBy(tempSort);
    setPage(1);
    setShowMobileFilters(false);
  }

  function handleResetFilters() {
    setTempCategory("");
    setTempProject("");
    setTempSort("newest");
  }

  const columns = [
    { field: "case_title", label: "Kasus", sortable: false, className: "min-w-[200px]" },
    { field: "causes", label: "Penyebab", sortable: false, className: "min-w-[150px]" },
    { field: "effect", label: "Dampak", sortable: false, className: "min-w-[150px]" },
    { field: "project_action", label: "Tindakan", sortable: false, className: "min-w-[150px]" },
    { field: "future_recommendation", label: "Rekomendasi", sortable: false, className: "min-w-[150px]" },
    { field: "project", label: "Project", sortable: false, className: "w-[150px]" },
    { field: "photos", label: "Foto", sortable: false, className: "w-[80px] text-center" },
    { field: "created_at", label: "Tanggal", sortable: false, className: "w-[100px]" },
  ];

  return (
    <div className="p-6 pt-20 max-w-[1400px] mx-auto overflow-x-hidden relative">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <div>
          <button
            onClick={() => navigate(ROUTES.PUBLIC_PORTAL)}
            className="flex items-center text-sm gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <Undo2 size={14} />
            <span>Kembali ke Portal Public</span>
          </button>
          <PageTitle>Dashboard Lesson Learned</PageTitle>
        </div> 
        <button
          onClick={() => setShowExportModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download size={18} />
          <span>Export Excel</span>
        </button>
      </div>

      {/* STATS + FILTERS + BREAKDOWN ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <div className="flex-1 flex flex-col gap-1 px-4 py-3 border rounded-lg bg-white shadow-sm">
              <p className="text-xs text-gray-500">Lesson & Learn</p>
              <p className="font-bold text-2xl text-gray-800">{stats?.total_lessons ?? totalLessons}</p>
            </div>
            <div className="flex-1 flex flex-col gap-1 px-4 py-3 border rounded-lg bg-white shadow-sm">
              <p className="text-xs text-gray-500">Divisi</p>
              <p className="font-bold text-2xl text-gray-800">{stats?.total_divisions ?? 0}</p>
            </div>
            <div className="flex-1 flex flex-col gap-1 px-4 py-3 border rounded-lg bg-white shadow-sm">
              <p className="text-xs text-gray-500">Proyek</p>
              <p className="font-bold text-2xl text-gray-800">{stats?.total_projects ?? projects.length}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <SearchBar
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Cari lesson..."
              />
            </div>
            {isMobile && (
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center gap-2 px-3 py-2 border bg-white border-gray-300 rounded-lg hover:border-blue-400 text-sm"
              >
                <Filter size={16} />
                Filter
              </button>
            )}
          </div>

          {!isMobile && (
            <div className="flex flex-wrap gap-2">
              <div className="w-44">
                <FilterDropdown
                  label="Kategori"
                  options={[
                    { value: "", label: "Semua Kategori" },
                    ...categories.map((cat) => ({
                      value: cat.id,
                      label: cat.name,
                    })),
                  ]}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  placeholder="Pilih Kategori"
                />
              </div>
              <div className="w-44">
                <FilterDropdown
                  label="Project"
                  options={[
                    { value: "", label: "Semua Project" },
                    ...(Array.isArray(projects) ? projects : []).map((proj) => ({
                      value: proj.id, 
                      
                      // UBAH BAGIAN LABEL MENJADI STRING BIASA SEPERTI INI:
                      label: proj.division ? `${proj.name} (${proj.division})` : proj.name,
                      
                    })),
                  ]}
                  value={selectedProject}
                  onChange={handleProjectChange}
                  placeholder="Pilih Project"
                />
              </div>
              <div className="w-36">
                <FilterDropdown
                  label="Urutkan"
                  options={[
                    { value: "newest", label: "Terbaru" },
                    { value: "oldest", label: "Terlama" },
                  ]}
                  value={sortBy}
                  onChange={handleSortChange}
                />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Per Divisi + Per Kategori cards */}
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <div className="w-full md:flex-1 bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden flex flex-col md:h-[180px] md:max-h-[180px]">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-2.5">
              <p className="text-white text-xs font-semibold tracking-wide">Jumlah per Divisi</p>
            </div>
            <div className="p-3 flex-1 overflow-y-auto">
              {stats?.per_division && stats.per_division.length > 0 ? (
                <div className="space-y-2">
                  {stats.per_division.map((div, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" />
                        <span className="text-xs text-gray-700 truncate max-w-[110px]">{div.name}</span>
                      </div>
                      <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full min-w-[28px] text-center">
                        {div.count}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-2">Belum ada data</p>
              )}
            </div>
          </div>

          <div className="w-full md:flex-1 bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden flex flex-col md:h-[180px] md:max-h-[180px]">
            <div className="bg-gradient-to-r from-sky-500 to-cyan-500 px-4 py-2.5">
              <p className="text-white text-xs font-semibold tracking-wide">Jumlah per Kategori</p>
            </div>
            <div className="p-3 flex-1 overflow-y-auto">
              {stats?.per_category && stats.per_category.length > 0 ? (
                <div className="space-y-2">
                  {stats.per_category.map((cat, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color || "#3B82F6" }}
                        />
                        <span className="text-xs text-gray-700 truncate max-w-[100px]">{cat.name}</span>
                      </div>
                      <span
                        className="text-xs font-bold px-2 py-0.5 rounded-full min-w-[28px] text-center"
                        style={{
                          backgroundColor: (cat.color || "#3B82F6") + "18",
                          color: cat.color || "#3B82F6",
                        }}
                      >
                        {cat.count}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-2">Belum ada data</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE FILTER PANEL */}
      <MobileFilterPanel
        isOpen={showMobileFilters}
        onToggle={(val) => setShowMobileFilters(typeof val === 'boolean' ? val : !showMobileFilters)}
        filters={[
          {
            label: "Kategori",
            options: [
              { value: "", label: "Semua Kategori" },
              ...categories.map((cat) => ({ value: cat.id, label: cat.name })),
            ],
            value: tempCategory,
            onChange: (val) => setTempCategory(val),
            placeholder: "Pilih Kategori",
          },
          {
            label: "Project",
            options: [
              { value: "", label: "Semua Project" },
              ...(Array.isArray(projects) ? projects : []).map((proj) => ({
                // JIKA BACKEND MINTA STRING NAMA: ganti proj.id di bawah ini menjadi proj.name
                value: proj.id, 
                label: proj.division ? `${proj.name} (${proj.division})` : proj.name,
              })),
            ],
            value: tempProject,
            onChange: (val) => setTempProject(val),
            placeholder: "Pilih Project",
          },
          {
            label: "Urutkan",
            options: [
              { value: "newest", label: "Terbaru" },
              { value: "oldest", label: "Terlama" },
            ],
            value: tempSort,
            onChange: (val) => setTempSort(val),
          },
        ]}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* INFO */}
      <div className="mb-4">
        <p className="text-gray-600">
          Menampilkan {lessons.length} dari {totalLessons} lessons
          {selectedCategory && (
            <span className="ml-2">
              (kategori: {getCategoryName(categories, parseInt(selectedCategory))})
            </span>
          )}
        </p>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={lessons}
        loading={loading}
        renderRow={(lesson) => {
          const categoryColor = getCategoryColor(categories, lesson.category_id);
          return (
            <tr
              key={lesson.id}
              className="hover:bg-gray-50 hover:cursor-pointer"
              onClick={() => navigate(`${ROUTES.LESSONS_DASHBOARD_DETAIL(lesson.id)}`)}
            >
              <td className="px-6 py-4">
                <div className="flex flex-col gap-1">
                  <span className="text-gray-900 ml-1.5">{lesson.case}</span>
                  <span
                    className="text-xs font-medium px-2 py-1 rounded-full w-fit"
                    style={{ backgroundColor: categoryColor, color: "#FFFFFF" }}
                  >
                    {getCategoryName(categories, lesson.category_id)}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs text-gray-600 line-clamp-2">{lesson.causes}</span>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs text-gray-600 line-clamp-2">{lesson.effect}</span>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs text-gray-600 line-clamp-2">{lesson.project_action}</span>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs text-gray-600 line-clamp-2">{lesson.future_recommendation}</span>
              </td>
              <td className="px-6 py-4">
                <div>
                  <span className="text-xs text-gray-600">{lesson.project_name || "-"}</span>
                  {lesson.project_division && (
                    <p className="text-xs text-gray-400 mt-0.5">{lesson.project_division}</p>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-1">
                  {lesson.photos && lesson.photos.length > 0 ? (
                    <>
                      {lesson.photos.slice(0, 2).map((photo, photoIdx) => (
                        <div
                          key={photoIdx}
                          className="w-10 h-10 rounded border border-gray-300 overflow-hidden bg-gray-100 flex-shrink-0"
                        >
                          <img
                            src={`${API_BASE_URL}/${photo}`}
                            alt={`Foto ${photoIdx + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        </div>
                      ))}
                      {lesson.photos.length > 2 && (
                        <div className="w-10 h-10 rounded border border-blue-300 bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-semibold text-blue-700">+{lesson.photos.length - 2}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-1 text-gray-400">
                      <Image size={16} />
                      <span className="text-xs">No photo</span>
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4">
                <span className="text-xs text-gray-600">{formatDateShort(lesson.created_at)}</span>
              </td>
            </tr>
          );
        }}
        emptyMessage="Belum ada lesson tersedia."
        pagination={{ page, totalPages, onPageChange: setPage }}
      />
      
      <ExportExcelModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        projects={projects}
        isPublic
      />

      {isChatOpen && (
        <div className="fixed bottom-24 left-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
          <LessonLearnedChat />
        </div>
      )}

      <button
        onClick={() => setIsChatOpen(!isChatOpen)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-xl shadow-blue-900/20 hover:scale-105 transition-all flex items-center justify-center group"
      >
        {isChatOpen ? (
          <X size={26} className="text-white" />
        ) : (
          <Bot size={26} className="text-white group-hover:animate-bounce" />
        )}
      </button>
    </div>
  );
}
